/* eslint-disable @typescript-eslint/naming-convention */
import { lowerFirst } from "app/helpers/strings/lowerFirst";

import type { GeneralResponse } from "../../@types/responseInterfaces";

import { BaseFetchService } from "./baseFetchService";
import type { FetchParam, GetRequestOptions } from "./types";

export const NoCacheHeaders = {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: 0,
};

export class GeneralFetchService<D = any> extends BaseFetchService<GeneralResponse<D>> {
    private static _instance = new GeneralFetchService();

    private constructor() {
        super();
    }

    public static getInstance() {
        if (!GeneralFetchService._instance) {
            GeneralFetchService._instance = new GeneralFetchService();
        }
        return GeneralFetchService._instance;
    }

    protected transform<T>(result: GeneralResponse<T>): T {
        return result.Result;
    }

    protected validate<T>(result: GeneralResponse<T>): string {
        return result.Error || "";
    }
}

export class DefaultFetchService<D = any> extends BaseFetchService<D> {
    private static _instance = new DefaultFetchService();

    private constructor() {
        super();
    }

    public static getInstance() {
        if (!DefaultFetchService._instance) {
            DefaultFetchService._instance = new DefaultFetchService();
        }
        return DefaultFetchService._instance;
    }

    protected transform<T>(result: T): T {
        return result;
    }

    protected validate<T>(result: T): string {
        const isValueInvalid = result === null || typeof result === "undefined";

        return isValueInvalid ? "Error" : ""; // TODO: default error
    }
}

type UncapitalizedKeys<T> = T extends (infer U)[]
    ? UncapitalizedKeys<U>[]
    : T extends object
      ? { [K in keyof T as Uncapitalize<string & K>]: UncapitalizedKeys<T[K]> }
      : T;

/**
 * A FetchService class that transforms the response data keys from PascalCase to camelCase.
 */
export class CamelCaseFetchService<R> extends BaseFetchService<R> {
    // The type checker fails to infer correctly the type of the result when transformation is applied because of how
    // the base class is implemented. This is a workaround to avoid the type checking error.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    get(url: string, params?: FetchParam, config: GetRequestOptions = {}): Promise<UncapitalizedKeys<R>> {
        return super.get(url, params, config);
    }

    protected transform(result: R): UncapitalizedKeys<R> {
        switch (true) {
            case Array.isArray(result):
                return result.map((item) => this.transform(item)) as UncapitalizedKeys<R>;
            case typeof result === "object" && result !== null:
                return Object.fromEntries(
                    Object.entries(result).map(([key, value]) => [lowerFirst(key), this.transform(value)]),
                ) as UncapitalizedKeys<R>;
            default:
                return result as UncapitalizedKeys<R>;
        }
    }

    // TODO(https://similarweb-rnd.atlassian.net/browse/PFG-215): Adopt Zod or other validation library to validate a response against schema
    protected validate<T>(result: T): string {
        return result === null ? "Error" : "";
    }
}
